import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { GetBankStatementResponse } from "@/models/api/finances/GetBankStatementsResponse";
import { FileTmp } from "@/models/api/Files";
import { axiosInstance } from "@/plugins/axios";
import { ParseBankStatementRequest } from "@/models/api/finances/ParseBankStatementRequest";
import { ParsePurchaseBankStatementRequest } from "@/models/api/finances/ParsePurchaseBankStatementRequest";
import { GetIsLockedEdit } from "@/models/api/finances/GetIsLockedEdit";

export const URL_GET_BANK_STATEMENTS = "/bankStatement/Paged";
export const URL_POST_MATCH_BANK_STATEMENT = "/bankStatement/${bankStatementId}/Match";
export const URL_POST_REMOVE_BANK_STATEMENT = "/bankStatement/${bankStatementId}/Remove";
export const URL_POST_UPLOAD_BANK_STATEMENT_FILE = "/File/UploadBankStatementFile";
export const URL_POST_UPLOAD_BANK_STATEMENT_PREVIEW = "/File/UploadBankStatementPreview/${BankStatementId}";
export const URL_POST_DELETE_BANK_STATEMENT_PREVIEW = "/File/DeleteBankStatementPreview/${BankStatementId}";
export const URL_POST_PARSE_BANK_STATEMENT = "/bankStatement/Parse";
export const URL_POST_PARSE_PURCHASE_BANK_STATEMENT = "/bankStatement/ParsePurchase";
export const URL_POST_PARSE_SIMP = "/bankStatement/ParseSIMP";
export const URL_POST_PARSE_FACTORING_STATEMENT = "/bankStatement/ParseFactoringStatement";
export const URL_GET_IS_LOCKED_BANK_STATEMENT_EDIT = "/bankStatement/${bankStatementId}/IsEditingLocked";
export const URL_GET_IS_LOCKED_BANK_STATEMENT_ITEM_EDIT = "/bankStatement/item/IsEditingLocked";
export const URL_GET_IS_LOCKED_BANK_STATEMENT_MATCH = "/bankStatement/item/IsBankStatementItemMatchLocked";
export const URL_GET_IS_LOCKED_BANK_STATEMENT_MATCH_PAYMENT =
  "/bankStatement/item/IsBankStatementItemMatchPaymentLocked";

const _getBankStatementsTable = new CancellableHelper<GetBankStatementResponse>(
  URL_GET_BANK_STATEMENTS,
  GetBankStatementResponse
);
_getBankStatementsTable.jsonSerializer = true;
export const getBankStatementsTable = async (
  genericTableRequest: GenericTableRequest
): Promise<GetBankStatementResponse> => {
  return await _getBankStatementsTable.get(genericTableRequest);
};

const _postPairBankTransfer = new CancellableHelper<void>(URL_POST_MATCH_BANK_STATEMENT);
export const postPairBankTransfer = async (bankStatementId: number): Promise<void> => {
  return await _postPairBankTransfer.post({ bankStatementId: bankStatementId }, null);
};

const _postRemoveBankTransfer = new CancellableHelper<void>(URL_POST_REMOVE_BANK_STATEMENT);
export const postRemoveBankTransfer = async (bankStatementId: number): Promise<void> => {
  return await _postRemoveBankTransfer.post({ bankStatementId: bankStatementId }, null);
};

export const postUploadBankStatementFile = async (files: Array<File>): Promise<Array<FileTmp>> => {
  const url = URL_POST_UPLOAD_BANK_STATEMENT_FILE;
  const formData = new FormData();
  for (const file of files) {
    formData.append(file.name, file);
  }
  const response = await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data as Array<FileTmp>;
};

export const postUploadBankStatementPreview = async (file: File, bankStatementId: number): Promise<void> => {
  const url = URL_POST_UPLOAD_BANK_STATEMENT_PREVIEW.replace("${BankStatementId}", bankStatementId.toString());
  const formData = new FormData();
  formData.append(file.name, file);
  await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const _postDeleteBankStatementPreview = new CancellableHelper<void>(URL_POST_DELETE_BANK_STATEMENT_PREVIEW);
export const postDeleteBankStatementPreview = async (bankStatementId: number): Promise<void> => {
  return await _postDeleteBankStatementPreview.post({ BankStatementId: bankStatementId }, null);
};

const _postParseBankStatement = new CancellableHelper<void>(URL_POST_PARSE_BANK_STATEMENT);
export const postParseBankStatement = async (request: ParseBankStatementRequest): Promise<void> => {
  return await _postParseBankStatement.post({}, request);
};

const _postParsePurchaseBankStatement = new CancellableHelper<void>(URL_POST_PARSE_PURCHASE_BANK_STATEMENT);
export const postParsePurchaseBankStatement = async (request: ParsePurchaseBankStatementRequest): Promise<void> => {
  return await _postParsePurchaseBankStatement.post({}, request);
};

const _postParseSIMP = new CancellableHelper<void>(URL_POST_PARSE_SIMP);
export const postParseSIMP = async (request: ParseBankStatementRequest): Promise<void> => {
  return await _postParseSIMP.post({}, request);
};

const _postParseFactoringStatement = new CancellableHelper<void>(URL_POST_PARSE_FACTORING_STATEMENT);
export const postParseFactoringStatement = async (request: ParseBankStatementRequest): Promise<void> => {
  return await _postParseFactoringStatement.post({}, request);
};

const _getIsLockedBankStatementEdit = new CancellableHelper<GetIsLockedEdit>(URL_GET_IS_LOCKED_BANK_STATEMENT_EDIT);
export const getIsLockedBankStatementEdit = async (bankStatementId: number): Promise<GetIsLockedEdit> => {
  return await _getIsLockedBankStatementEdit.get({
    bankStatementId: bankStatementId,
  });
};

const _getIsLockedBankStatementItemEdit = new CancellableHelper<GetIsLockedEdit>(
  URL_GET_IS_LOCKED_BANK_STATEMENT_ITEM_EDIT
);
export const getIsLockedBankStatementItemEdit = async (
  bankStatementItemIds: Array<number>
): Promise<GetIsLockedEdit> => {
  return await _getIsLockedBankStatementItemEdit.get({
    bankStatementItemIds: bankStatementItemIds,
  });
};

const _getIsLockedBankStatementMatch = new CancellableHelper<GetIsLockedEdit>(URL_GET_IS_LOCKED_BANK_STATEMENT_MATCH);
export const getIsLockedBankStatementMatch = async (
  bankStatementItemId: number,
  documentId: string
): Promise<GetIsLockedEdit> => {
  return await _getIsLockedBankStatementMatch.get({
    bankStatementItemId: bankStatementItemId,
    documentId: documentId,
  });
};

const _getIsLockedBankStatementMatchPayment = new CancellableHelper<GetIsLockedEdit>(
  URL_GET_IS_LOCKED_BANK_STATEMENT_MATCH_PAYMENT
);
export const getIsLockedBankStatementMatchPayment = async (
  bankStatementItemId: number,
  paymentId: number
): Promise<GetIsLockedEdit> => {
  return await _getIsLockedBankStatementMatchPayment.get({
    bankStatementItemId: bankStatementItemId,
    paymentId: paymentId,
  });
};
